import {
  BellRing,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Download,
  ExternalLink,
  Loader2,
  Plus,
  SlidersHorizontal,
  X,
} from "lucide-react";
import { Fragment, useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { AddApplication } from "@/components/applications/add-application";
import { DecideButton } from "@/components/applications/decision-panel";
import { PrioritiesPanel } from "@/components/applications/priorities-panel";
import { StudyPanel } from "@/components/applications/study-panel";
import { Page } from "@/components/layout/page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Input, Textarea } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import type {
  ApplicationStatus,
  ApplicationTracker,
  DecisionPriorities,
  TrackedApplication,
  TrackerUpdate,
} from "@/lib/types";
import { cn, fitPercent } from "@/lib/utils";
import { VERDICT_LABEL, VERDICT_VARIANT } from "@/lib/verdict";

const STATUSES: ApplicationStatus[] = [
  "interested",
  "applied",
  "screening",
  "interview",
  "offer",
  "rejected",
  "withdrawn",
];

const CLOSED: ApplicationStatus[] = ["rejected", "withdrawn"];

type Filter = "open" | "all" | ApplicationStatus;

/**
 * The application tracker: every posting you are pursuing, in one table you
 * can edit like a spreadsheet.
 *
 * A row appears when you mark a job on the Jobs page or approve a document for
 * it. Each cell saves on its own when you leave it – there is no Save button
 * to forget. The agent keeps the same table: tell it in Chat (Jobs mode) what
 * happened, and it writes the note, the next step and the date here.
 *
 * "Should I apply?" is a fixed rule over your own priorities, not a model's
 * opinion – see `components/applications/decision-panel.tsx`.
 */
export function ApplicationsRoute() {
  const [tracker, setTracker] = useState<ApplicationTracker | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>("open");
  const [showPriorities, setShowPriorities] = useState(false);
  const [adding, setAdding] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setTracker(await api.applications.list());
      setError(null);
    } catch (reason) {
      setError((reason as Error).message);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    api.applications
      .list()
      .then((next) => !cancelled && setTracker(next))
      .catch((reason: Error) => !cancelled && setError(reason.message));
    return () => {
      cancelled = true;
    };
  }, []);

  function replaceRow(row: TrackedApplication) {
    setTracker((current) =>
      current
        ? {
            ...current,
            rows: current.rows.map((existing) =>
              existing.jobId === row.jobId ? row : existing,
            ),
            due:
              current.due -
              (current.rows.find((existing) => existing.jobId === row.jobId)
                ?.followUpDue
                ? 1
                : 0) +
              (row.followUpDue ? 1 : 0),
          }
        : current,
    );
  }

  function prioritiesSaved(priorities: DecisionPriorities) {
    // Every verdict depends on them: reload so each row is re-decided (free).
    setTracker((current) => (current ? { ...current, priorities } : current));
    void load();
  }

  const rows = (tracker?.rows ?? []).filter((row) =>
    filter === "all"
      ? true
      : filter === "open"
        ? !CLOSED.includes(row.status)
        : row.status === filter,
  );
  const due = (tracker?.rows ?? []).filter((row) => row.followUpDue);

  return (
    <Page
      tone="work"
      plain
      eyebrow="Tracker"
      title="Applications"
      description="Every posting you are pursuing, what happens next, and whether it is worth applying."
      actions={
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAdding((open) => !open)}
          >
            <Plus className="size-3.5" aria-hidden />
            Add an application
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowPriorities((open) => !open)}
          >
            <SlidersHorizontal className="size-3.5" aria-hidden />
            {showPriorities ? "Hide priorities" : "What matters to me"}
          </Button>
        </div>
      }
    >
      <div className="space-y-5 px-5 py-6 lg:px-8">
        {error ? (
          <p className="rounded-lg border bg-card/50 px-4 py-3 text-sm text-muted-foreground">
            {error}
          </p>
        ) : null}

        {showPriorities && tracker ? (
          <PrioritiesPanel
            priorities={tracker.priorities}
            onSaved={prioritiesSaved}
            onClose={() => setShowPriorities(false)}
          />
        ) : null}

        {adding ? (
          <AddApplication
            onAdded={(row) => {
              setAdding(false);
              setFilter("all");
              setExpanded(row.jobId);
              void load();
            }}
            onClose={() => setAdding(false)}
          />
        ) : null}

        {due.length > 0 ? <FollowUps rows={due} /> : null}

        {tracker === null && error === null ? (
          <div className="space-y-2">
            {[0, 1, 2].map((index) => (
              <Skeleton key={index} className="h-12" />
            ))}
          </div>
        ) : null}

        {tracker && tracker.rows.length === 0 ? (
          <EmptyState
            icon={ClipboardList}
            title="Nothing tracked yet"
            description="Mark a posting on the Jobs page – interested, applied, anything – or tailor a CV for it, and it appears here. Applied somewhere else? Use “Add an application”."
            action={
              <Link to="/jobs" className="text-sm text-primary hover:underline">
                Go to Jobs
              </Link>
            }
          />
        ) : null}

        {tracker && tracker.rows.length > 0 ? (
          <section className="space-y-3 rounded-2xl border bg-card/50 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="font-semibold">Your applications</h2>
                <p className="text-xs text-muted-foreground">
                  Click a cell to edit it; it saves when you leave it. Or tell
                  the agent in Chat (Jobs mode): &ldquo;Had a call with Bosch,
                  second round on Friday&rdquo;.
                </p>
              </div>
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                Show
                <select
                  className="h-8 rounded-md border bg-transparent px-2 text-sm text-foreground"
                  value={filter}
                  onChange={(event) => setFilter(event.target.value as Filter)}
                >
                  <option value="open">Open ({countOpen(tracker.rows)})</option>
                  <option value="all">All ({tracker.rows.length})</option>
                  {STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {rows.length === 0 ? (
              <p className="py-4 text-sm text-muted-foreground">
                Nothing with that status.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[920px] text-sm">
                  <thead className="text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="w-6" aria-label="Details" />
                      <th className="py-1.5 pr-3 font-medium">Posting</th>
                      <th className="py-1.5 pr-3 font-medium">Status</th>
                      <th className="py-1.5 pr-3 font-medium">Applied</th>
                      <th className="py-1.5 pr-3 font-medium">Next step</th>
                      <th className="py-1.5 pr-3 font-medium">Follow up</th>
                      <th className="py-1.5 pr-3 font-medium">Fit</th>
                      <th className="py-1.5 font-medium">Should I apply?</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <Fragment key={row.jobId}>
                        <TrackerRow
                          row={row}
                          expanded={expanded === row.jobId}
                          onToggle={() =>
                            setExpanded((current) =>
                              current === row.jobId ? null : row.jobId,
                            )
                          }
                          onSaved={replaceRow}
                        />
                        {expanded === row.jobId ? (
                          <tr className="bg-muted/20">
                            <td />
                            <td colSpan={7} className="px-1 pb-4 pt-2">
                              <RowDetails
                                row={row}
                                onSaved={replaceRow}
                                onUntracked={() => {
                                  setExpanded(null);
                                  void load();
                                }}
                                onDecided={() => void load()}
                              />
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        ) : null}
      </div>
    </Page>
  );
}

function countOpen(rows: TrackedApplication[]): number {
  return rows.filter((row) => !CLOSED.includes(row.status)).length;
}

function FollowUps({ rows }: { rows: TrackedApplication[] }) {
  return (
    <section className="rounded-2xl border border-warning/40 bg-warning/5 p-4">
      <h2 className="flex items-center gap-2 font-semibold">
        <BellRing className="size-4 text-warning" aria-hidden />
        {rows.length} follow-up{rows.length === 1 ? "" : "s"} due
      </h2>
      <ul className="mt-2 space-y-1 text-sm">
        {rows.map((row) => (
          <li key={row.jobId}>
            <span className="font-medium">{row.company}</span>
            <span className="text-muted-foreground"> · {row.title} – </span>
            {row.followUpDue}
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Save one change and hand the updated row back. */
function useSave(jobId: string, onSaved: (row: TrackedApplication) => void) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function save(changes: TrackerUpdate) {
    setSaving(true);
    setError(null);
    try {
      onSaved(await api.applications.update(jobId, changes));
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setSaving(false);
    }
  }
  return { save, saving, error };
}

function TrackerRow({
  row,
  expanded,
  onToggle,
  onSaved,
}: {
  row: TrackedApplication;
  expanded: boolean;
  onToggle: () => void;
  onSaved: (row: TrackedApplication) => void;
}) {
  const { save, saving, error } = useSave(row.jobId, onSaved);

  return (
    <tr
      className={cn(
        "border-t border-border/50 align-top",
        saving && "opacity-70",
      )}
    >
      <td className="py-2">
        <button
          type="button"
          onClick={onToggle}
          className="rounded p-0.5 text-muted-foreground hover:text-foreground"
          aria-label={
            expanded ? "Hide details" : "Show notes, documents and the decision"
          }
          aria-expanded={expanded}
        >
          {expanded ? (
            <ChevronDown className="size-4" aria-hidden />
          ) : (
            <ChevronRight className="size-4" aria-hidden />
          )}
        </button>
      </td>
      <td className="max-w-[240px] py-2 pr-3">
        <p className="truncate font-medium" title={row.title}>
          {row.title}
        </p>
        <p className="truncate text-xs text-muted-foreground">
          {row.company}
          {row.location ? ` · ${row.location}` : ""}
          {row.url ? (
            <a
              href={row.url}
              target="_blank"
              rel="noreferrer noopener"
              className="ml-1 inline-flex items-center text-primary"
              aria-label="Open the posting"
            >
              <ExternalLink className="size-3" aria-hidden />
            </a>
          ) : null}
        </p>
        {row.study ? (
          <Badge variant="outline" className="mt-1 px-1.5 py-0 text-[10px]">
            {row.study.bestScore !== null
              ? `quiz ${row.study.bestScore}/${row.study.questions}`
              : row.study.questions > 0
                ? "study pack ready"
                : "nothing to study"}
          </Badge>
        ) : null}
        {error ? <p className="text-xs text-destructive">{error}</p> : null}
      </td>
      <td className="py-2 pr-3">
        <select
          aria-label={`Status of ${row.title}`}
          className="h-8 rounded-md border bg-transparent px-2 text-sm"
          value={row.status}
          disabled={saving}
          onChange={(event) =>
            void save({ status: event.target.value as ApplicationStatus })
          }
        >
          {STATUSES.map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>
      </td>
      <td className="py-2 pr-3">
        <DateCell
          key={row.appliedOn ?? ""}
          label={`Applied on, ${row.title}`}
          value={row.appliedOn}
          onSave={(value) => save({ appliedOn: value })}
        />
      </td>
      <td className="py-2 pr-3">
        <TextCell
          key={row.nextStep ?? ""}
          label={`Next step, ${row.title}`}
          value={row.nextStep}
          placeholder="e.g. Tech interview"
          onSave={(value) => save({ nextStep: value })}
        />
      </td>
      <td className="py-2 pr-3">
        <DateCell
          key={row.followUpOn ?? ""}
          label={`Follow up on, ${row.title}`}
          value={row.followUpOn}
          onSave={(value) => save({ followUpOn: value })}
        />
        {row.followUpDue ? (
          <Badge
            variant="warning"
            className="mt-1 whitespace-nowrap px-1.5 py-0 text-[10px]"
          >
            due
          </Badge>
        ) : null}
      </td>
      <td className="tabular py-2 pr-3 text-xs">
        {row.fitScore !== null ? `${fitPercent(row.fitScore)}%` : "–"}
      </td>
      <td className="py-2">
        {row.decision ? (
          <button type="button" onClick={onToggle} className="text-left">
            <Badge variant={VERDICT_VARIANT[row.decision.verdict]}>
              {VERDICT_LABEL[row.decision.verdict]}
            </Badge>
            {row.decision.score !== null ? (
              <span className="tabular ml-1.5 text-xs text-muted-foreground">
                {row.decision.score}
              </span>
            ) : null}
          </button>
        ) : (
          <button
            type="button"
            onClick={onToggle}
            className="text-xs text-primary hover:underline"
          >
            Decide
          </button>
        )}
      </td>
    </tr>
  );
}

function TextCell({
  label,
  value,
  placeholder,
  onSave,
}: {
  label: string;
  value: string | null;
  placeholder?: string;
  onSave: (value: string) => Promise<void>;
}) {
  // Re-created by the parent (its `key`) whenever the saved value changes, so
  // the draft starts from what was saved without an effect copying it over.
  const [draft, setDraft] = useState(value ?? "");
  return (
    <Input
      aria-label={label}
      className="h-8 min-w-[150px]"
      value={draft}
      placeholder={placeholder}
      maxLength={300}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => {
        if (draft.trim() !== (value ?? "")) void onSave(draft.trim());
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter") event.currentTarget.blur();
      }}
    />
  );
}

/** Saves when you leave the field, not on every keystroke: typing a year
 *  passes through several valid dates on the way (0002, 0020, 0202, 2026). */
function DateCell({
  label,
  value,
  onSave,
}: {
  label: string;
  value: string | null;
  onSave: (value: string) => Promise<void>;
}) {
  const [draft, setDraft] = useState(value ?? "");
  return (
    <Input
      type="date"
      aria-label={label}
      className="h-8 w-[140px]"
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => {
        if (draft !== (value ?? "")) void onSave(draft);
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter") event.currentTarget.blur();
      }}
    />
  );
}

function RowDetails({
  row,
  onSaved,
  onUntracked,
  onDecided,
}: {
  row: TrackedApplication;
  onSaved: (row: TrackedApplication) => void;
  onUntracked: () => void;
  onDecided: () => void;
}) {
  const [confirming, setConfirming] = useState(false);

  async function untrack() {
    if (!confirming) {
      setConfirming(true);
      return;
    }
    await api.applications.untrack(row.jobId);
    onUntracked();
  }

  return (
    <div className="space-y-5">
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]">
        <div className="space-y-3">
          <NotesEditor key={row.note ?? ""} row={row} onSaved={onSaved} />
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              className={cn("ml-auto", confirming && "text-destructive")}
              onClick={() => void untrack()}
              onBlur={() => setConfirming(false)}
            >
              <X className="size-3.5" aria-hidden />
              {confirming ? "Click again to remove" : "Stop tracking"}
            </Button>
          </div>

          <p className="text-sm">
            <Link
              to={`/interview?job=${encodeURIComponent(row.jobId)}`}
              className="text-primary hover:underline"
            >
              Practise the interview for this posting
            </Link>
          </p>

          <div>
            <p className="text-xs text-muted-foreground">
              Documents for this posting
            </p>
            {row.documents.length === 0 ? (
              <p className="text-sm">
                None yet –{" "}
                <Link
                  to={`/documents?job=${encodeURIComponent(row.jobId)}`}
                  className="text-primary hover:underline"
                >
                  tailor a CV
                </Link>
              </p>
            ) : (
              <ul className="mt-1 space-y-1">
                {row.documents.map((doc) => (
                  <li key={doc.id}>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                      onClick={() =>
                        void api.documents.download(doc.id, "docx")
                      }
                    >
                      <Download className="size-3.5" aria-hidden />
                      {doc.stem}.docx
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            A fixed rule over your priorities and what the posting says – the
            model only reads the posting.
          </p>
          <DecideButton
            jobId={row.jobId}
            initial={row.decision}
            onDecided={onDecided}
          />
        </div>
      </div>
      <div className="border-t pt-4">
        <StudyPanel jobId={row.jobId} onChanged={onDecided} />
      </div>
    </div>
  );
}

/** Keyed by the saved note, so a save (or the agent adding a line) starts it afresh. */
function NotesEditor({
  row,
  onSaved,
}: {
  row: TrackedApplication;
  onSaved: (row: TrackedApplication) => void;
}) {
  const { save, saving, error } = useSave(row.jobId, onSaved);
  const [note, setNote] = useState(row.note ?? "");
  return (
    <div className="space-y-2">
      <label className="block space-y-1 text-sm">
        <span className="text-xs text-muted-foreground">Notes</span>
        <Textarea
          value={note}
          rows={5}
          maxLength={4000}
          placeholder="Recruiter, salary talk, what they asked…"
          onChange={(event) => setNote(event.target.value)}
        />
      </label>
      <Button
        size="sm"
        variant="outline"
        disabled={saving || note === (row.note ?? "")}
        onClick={() => void save({ note })}
      >
        {saving ? (
          <Loader2 className="size-3.5 animate-spin" aria-hidden />
        ) : null}
        Save notes
      </Button>
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}
